import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Checkbox,
  Chip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PublicIcon from "@mui/icons-material/Public";
import LockIcon from "@mui/icons-material/Lock";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { useSnackbar } from "notistack";
import { Image, useUpdateImage } from "../../hooks/useImages";
import { useUpdateAlbum, useAlbums } from "../../hooks/useAlbums";
import { CategoryResponse, SubcategoryResponse } from "../../api/generated";
import { getImageId, formatDate } from "../../utils/imageUtils";

interface ImageListItemProps {
  image: Image;
  selected: boolean;
  onSelect: (imageId: string, selected: boolean) => void;
  onDelete: (image: Image) => void;
  onToggleVisibility: (image: Image) => void;
  sortBy?: string;
  sortOrder?: string;
  categories?: CategoryResponse[];
  subcategories?: SubcategoryResponse[];
  galleries?: Array<{ id: string; title: string }>;
}

export const ImageListItem: React.FC<ImageListItemProps> = ({
  image,
  selected,
  onSelect,
  onDelete,
  onToggleVisibility,
  sortBy,
  sortOrder,
  categories = [],
  subcategories = [],
  galleries = [],
}) => {
  const updateImage = useUpdateImage({ showSuccessMessage: false });
  const updateAlbum = useUpdateAlbum();
  const { data: albumsData } = useAlbums({ page_size: 100 });
  const { enqueueSnackbar } = useSnackbar();

  const [title, setTitle] = useState(image.title || "");
  const [description, setDescription] = useState(image.description || "");
  const [categoryId, setCategoryId] = useState<string>(image.category_id || "");
  const [subcategoryId, setSubcategoryId] = useState<string>(image.subcategory_id || "");
  const [selectedGalleryIds, setSelectedGalleryIds] = useState<string[]>([]);
  const [galleryImageMap, setGalleryImageMap] = useState<Record<string, string[]>>({});
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);

  // Load gallery associations
  useEffect(() => {
    const loadGalleryImages = async () => {
      if (!albumsData?.items) return;

      const imageId = getImageId(image);
      const map: Record<string, string[]> = {};
      const containingGalleries: string[] = [];

      try {
        const { getAlbumImagesApiAdminAlbumsAlbumIdImagesGet } = await import("../../api/generated");

        for (const album of albumsData.items) {
          try {
            // Use admin endpoint to access both public and private albums
            const imagesResponse = await getAlbumImagesApiAdminAlbumsAlbumIdImagesGet({
              path: { album_id: album.id },
            });

            if (imagesResponse.data) {
              const imageIds = (imagesResponse.data.items || [])
                .map((img: any) => img.id || img.cloudinary_public_id)
                .filter(Boolean);
              map[album.id] = imageIds;

              if (imageIds.includes(imageId)) {
                containingGalleries.push(album.id);
              }
            }
          } catch (error) {
            console.error(`Failed to load images for album ${album.id}:`, error);
          }
        }

        setGalleryImageMap(map);
        setSelectedGalleryIds(containingGalleries);
      } catch (error) {
        console.error("Failed to load gallery images:", error);
      }
    };

    loadGalleryImages();
  }, [image, albumsData]);

  // Update local state when image changes
  useEffect(() => {
    setTitle(image.title || "");
    setDescription(image.description || "");
    setCategoryId(image.category_id || "");
    setSubcategoryId(image.subcategory_id || "");
  }, [image]);

  // Generate optimized image URL for compact preview
  const imageUrl = image.cloudinary_url.replace(
    "/upload/",
    "/upload/w_150,h_150,c_fill/"
  );


  // Filter subcategories by selected category
  const availableSubcategories = subcategories.filter(
    (sub) => sub.category_id === categoryId
  );

  // Check if there are unsaved changes
  const hasUnsavedChanges = title !== (image.title || "") || description !== (image.description || "");

  // Handle title update
  const handleTitleBlur = () => {
    setEditingTitle(false);
    // Don't auto-save on blur anymore - user must click save
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      setTitle(image.title || "");
      setEditingTitle(false);
    }
  };

  // Handle description update
  const handleDescriptionBlur = () => {
    setEditingDescription(false);
    // Don't auto-save on blur anymore - user must click save
  };

  // Handle save all changes
  const handleSave = async () => {
    const imageId = getImageId(image);
    const updates: { title?: string | null; description?: string | null } = {};

    if (title !== (image.title || "")) {
      updates.title = title || null;
    }
    if (description !== (image.description || "")) {
      updates.description = description || null;
    }

    if (Object.keys(updates).length > 0) {
      await updateImage.mutateAsync({
        imageId,
        data: updates,
      });
      setEditingTitle(false);
      setEditingDescription(false);
    }
  };

  const handleDescriptionKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      setDescription(image.description || "");
      setEditingDescription(false);
    }
  };

  // Handle category update
  const handleCategoryChange = async (newCategoryId: string) => {
    setCategoryId(newCategoryId);
    setSubcategoryId(""); // Clear subcategory when category changes

    const imageId = getImageId(image);
    await updateImage.mutateAsync({
      imageId,
      data: {
        category_id: newCategoryId || null,
        subcategory_id: null,
      },
    });
  };

  // Handle subcategory update
  const handleSubcategoryChange = async (newSubcategoryId: string) => {
    setSubcategoryId(newSubcategoryId);

    const imageId = getImageId(image);
    await updateImage.mutateAsync({
      imageId,
      data: { subcategory_id: newSubcategoryId || null },
    });
  };

  // Handle gallery changes
  const handleGalleryChange = async (newGalleryIds: string[]) => {
    const imageId = getImageId(image);
    const initialGalleryIds = Object.keys(galleryImageMap).filter(
      (albumId) => galleryImageMap[albumId]?.includes(imageId)
    );

    // Find galleries to add
    const galleriesToAdd = newGalleryIds.filter((id) => !initialGalleryIds.includes(id));
    // Find galleries to remove
    const galleriesToRemove = initialGalleryIds.filter((id) => !newGalleryIds.includes(id));

    try {
      const { getAlbumImagesApiAdminAlbumsAlbumIdImagesGet } = await import("../../api/generated");
      const updatePromises: Promise<any>[] = [];

      // Add to galleries - fetch fresh data for each
      for (const albumId of galleriesToAdd) {
        // Fetch current gallery images from API to avoid using stale data
        const imagesResponse = await getAlbumImagesApiAdminAlbumsAlbumIdImagesGet({
          path: { album_id: albumId },
        });

        const currentImageIds = imagesResponse.data
          ? (imagesResponse.data.items || [])
              .map((img: any) => img.id || img.cloudinary_public_id)
              .filter(Boolean) as string[]
          : [];

        // Only add if not already in gallery
        if (!currentImageIds.includes(imageId)) {
          const newImageIds = [...currentImageIds, imageId];
          updatePromises.push(
            updateAlbum.mutateAsync({
              albumId,
              data: { image_ids: newImageIds },
            })
          );
        }
      }

      // Remove from galleries - fetch fresh data for each
      for (const albumId of galleriesToRemove) {
        // Fetch current gallery images from API to avoid using stale data
        const imagesResponse = await getAlbumImagesApiAdminAlbumsAlbumIdImagesGet({
          path: { album_id: albumId },
        });

        const currentImageIds = imagesResponse.data
          ? (imagesResponse.data.items || [])
              .map((img: any) => img.id || img.cloudinary_public_id)
              .filter(Boolean) as string[]
          : [];

        const newImageIds = currentImageIds.filter((id) => id !== imageId);
        updatePromises.push(
          updateAlbum.mutateAsync({
            albumId,
            data: { image_ids: newImageIds },
          })
        );
      }

      await Promise.all(updatePromises);
      setSelectedGalleryIds(newGalleryIds);

      // Update local map
      const updatedMap = { ...galleryImageMap };
      galleriesToAdd.forEach((albumId) => {
        updatedMap[albumId] = [...(updatedMap[albumId] || []), imageId];
      });
      galleriesToRemove.forEach((albumId) => {
        updatedMap[albumId] = (updatedMap[albumId] || []).filter((id) => id !== imageId);
      });
      setGalleryImageMap(updatedMap);
    } catch (error) {
      console.error("Failed to update galleries:", error);
      enqueueSnackbar("Failed to update gallery associations. Reloading...", {
        variant: "error",
      });
      // Reload gallery associations on error
      const loadGalleryImages = async () => {
        if (!albumsData?.items) return;

        const map: Record<string, string[]> = {};
        const containingGalleries: string[] = [];

        try {
          const { getAlbumImagesApiAdminAlbumsAlbumIdImagesGet } = await import("../../api/generated");

          for (const album of albumsData.items) {
            try {
              const imagesResponse = await getAlbumImagesApiAdminAlbumsAlbumIdImagesGet({
                path: { album_id: album.id },
              });

              if (imagesResponse.data) {
                const imageIds = (imagesResponse.data.items || [])
                  .map((img: any) => img.id || img.cloudinary_public_id)
                  .filter(Boolean);
                map[album.id] = imageIds;

                if (imageIds.includes(imageId)) {
                  containingGalleries.push(album.id);
                }
              }
            } catch (error) {
              console.error(`Failed to load images for album ${album.id}:`, error);
            }
          }

          setGalleryImageMap(map);
          setSelectedGalleryIds(containingGalleries);
        } catch (error) {
          console.error("Failed to reload gallery images:", error);
        }
      };

      await loadGalleryImages();
    }
  };

  // Helper function to check if a field is being sorted
  const isSortedBy = (field: string) => sortBy === field;
  const getSortIndicator = (field: string) => {
    if (!isSortedBy(field)) return "";
    return sortOrder === "asc" ? " ↑" : " ↓";
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1, sm: 1.5 },
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 0,
        backgroundColor: selected ? "action.selected" : "background.paper",
        transition: (theme) => `all ${theme.custom.transitions.fast}`,
        "&:hover": {
          borderColor: "primary.main",
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 0.75, sm: 1 } }}>
        {/* Header with Checkbox and Image */}
        <Box sx={{ display: "flex", gap: { xs: 0.75, sm: 1 }, alignItems: "flex-start" }}>
          <Checkbox
            checked={selected}
            onChange={(e) => {
              e.stopPropagation();
              onSelect(getImageId(image), e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            size="small"
            sx={{ mt: { xs: -0.5, sm: -1 }, ml: { xs: -0.5, sm: -1 } }}
          />
          <Box
            sx={{
              width: { xs: 80, sm: 120 },
              height: { xs: 80, sm: 120 },
              flexShrink: 0,
              borderRadius: 0,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Box
              component="img"
              src={imageUrl}
              alt={image.alt_text || image.title || "Image"}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 }, mb: { xs: 0.5, sm: 1 } }}>
              <Typography
                variant="caption"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleVisibility(image);
                }}
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                  color: image.is_public ? "success.main" : "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  cursor: "pointer",
                }}
              >
                {image.is_public ? <PublicIcon fontSize="small" /> : <LockIcon fontSize="small" />}
                {image.is_public ? "Public" : "Private"}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: { xs: "0.65rem", sm: "0.7rem" } }}>
                {image.width} × {image.height} • {(image.file_size / 1024 / 1024).toFixed(2)} MB
                {getSortIndicator("file_size")}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: { xs: "0.65rem", sm: "0.7rem" }, display: "block" }}>
              {formatDate(image.created_at)}
              {getSortIndicator("created_at")} • {image.view_count || 0} views
            </Typography>
          </Box>
        </Box>

        {/* Title - Inline Editable */}
        {editingTitle ? (
          <TextField
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={handleTitleBlur}
            onKeyDown={handleTitleKeyDown}
            placeholder="Image title"
            size="small"
            fullWidth
            autoFocus
            disabled={updateImage.isPending}
            variant="standard"
            sx={{
              "& .MuiInputBase-input": {
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                py: { xs: 0.25, sm: 0.375 },
              },
              "& .MuiInput-underline:before": {
                borderBottom: "none",
              },
              "& .MuiInput-underline:hover:before": {
                borderBottom: "none",
              },
              "& .MuiInput-underline:after": {
                borderBottom: "none",
              },
            }}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              py: 0.5,
              "&:hover": {
                opacity: 0.7,
              },
            }}
            onClick={() => setEditingTitle(true)}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                color: title ? "text.primary" : "text.secondary",
                fontStyle: title ? "normal" : "italic",
                flex: 1,
              }}
            >
              {title || "add title"}
            </Typography>
            <EditIcon sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, color: "text.secondary" }} />
          </Box>
        )}

        {/* Description - Inline Editable */}
        {editingDescription ? (
          <TextField
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={handleDescriptionBlur}
            onKeyDown={handleDescriptionKeyDown}
            placeholder="Description"
            size="small"
            fullWidth
            autoFocus
            disabled={updateImage.isPending}
            variant="standard"
            sx={{
              "& .MuiInputBase-input": {
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                py: { xs: 0.25, sm: 0.375 },
              },
              "& .MuiInput-underline:before": {
                borderBottom: "none",
              },
              "& .MuiInput-underline:hover:before": {
                borderBottom: "none",
              },
              "& .MuiInput-underline:after": {
                borderBottom: "none",
              },
            }}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              py: { xs: 0.25, sm: 0.375 },
              "&:hover": {
                opacity: 0.7,
              },
            }}
            onClick={() => setEditingDescription(true)}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                color: description ? "text.primary" : "text.secondary",
                fontStyle: description ? "normal" : "italic",
                flex: 1,
              }}
            >
              {description || "add description"}
            </Typography>
            <EditIcon sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, color: "text.secondary" }} />
          </Box>
        )}

        {/* Category and Subcategory */}
        <Box sx={{ display: "flex", gap: { xs: 0.5, sm: 1 }, flexDirection: { xs: "column", sm: "row" } }}>
          <FormControl size="small" fullWidth variant="standard">
            <InputLabel sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>Category</InputLabel>
            <Select
              value={categoryId}
              onChange={(e) => handleCategoryChange(e.target.value)}
              label="Category"
              disabled={updateImage.isPending}
              sx={{
                fontSize: { xs: "0.65rem", sm: "0.75rem" },
                "& .MuiInput-underline:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:hover:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:after": {
                  borderBottom: "none",
                },
              }}
            >
              <MenuItem value="" sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>
                <em>None</em>
              </MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth disabled={!categoryId} variant="standard">
            <InputLabel sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>Subcategory</InputLabel>
            <Select
              value={subcategoryId}
              onChange={(e) => handleSubcategoryChange(e.target.value)}
              label="Subcategory"
              disabled={updateImage.isPending || !categoryId}
              sx={{
                fontSize: { xs: "0.65rem", sm: "0.75rem" },
                "& .MuiInput-underline:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:hover:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:after": {
                  borderBottom: "none",
                },
              }}
            >
              <MenuItem value="" sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>
                <em>None</em>
              </MenuItem>
              {availableSubcategories.map((sub) => (
                <MenuItem key={sub.id} value={sub.id} sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>
                  {sub.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Galleries */}
        <FormControl size="small" fullWidth variant="standard">
          <InputLabel sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>Galleries</InputLabel>
          <Select
            multiple
            value={selectedGalleryIds}
            onChange={(e) => handleGalleryChange(e.target.value as string[])}
            label="Galleries"
            disabled={updateAlbum.isPending}
            renderValue={(selected) => {
              const selectedTitles = (selected as string[])
                .map((id) => galleries.find((g) => g.id === id)?.title)
                .filter(Boolean);
              return (
                <Typography sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>
                  {selectedTitles.length > 0 ? selectedTitles.join(", ") : "None"}
                </Typography>
              );
            }}
            sx={{
              fontSize: { xs: "0.65rem", sm: "0.75rem" },
              "& .MuiInput-underline:before": {
                borderBottom: "none",
              },
              "& .MuiInput-underline:hover:before": {
                borderBottom: "none",
              },
              "& .MuiInput-underline:after": {
                borderBottom: "none",
              },
            }}
          >
            {galleries.map((gallery) => (
              <MenuItem key={gallery.id} value={gallery.id} sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>
                {gallery.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Save and Delete Buttons */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: { xs: 0.5, sm: 0.75 }, pt: { xs: 0.25, sm: 0.5 } }}>
          <Button
            variant="text"
            startIcon={<SaveIcon />}
            disableRipple
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSave();
            }}
            disabled={!hasUnsavedChanges || updateImage.isPending}
            sx={{
              textTransform: "none",
              color: "primary.main",
              px: { xs: 0.5, sm: 1 },
              minWidth: "auto",
              fontSize: { xs: "0.65rem", sm: "0.75rem" },
              "& .MuiButton-startIcon": {
                marginRight: { xs: 0.25, sm: 0.5 },
              },
            }}
          >
            Save
          </Button>
          <Button
            variant="text"
            startIcon={<DeleteIcon />}
            disableRipple
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDelete(image);
            }}
            sx={{
              textTransform: "none",
              color: "error.main",
              px: { xs: 0.5, sm: 1 },
              minWidth: "auto",
              fontSize: { xs: "0.65rem", sm: "0.75rem" },
              "& .MuiButton-startIcon": {
                marginRight: { xs: 0.25, sm: 0.5 },
              },
            }}
          >
            Delete
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};
